import { useTranslation } from "react-i18next";
import { PortalVisitResponse } from "../../features/portal/contracts/PortalVisitResponse";
import { fallbackVisitStatusStyle, visitStatusStyles } from "./visitStatusStyles";

interface PortalVisitHistoryProps {
  visits: PortalVisitResponse[] | undefined;
}

/** The member's recorded visits with outcomes, newest first. */
const PortalVisitHistory = ({ visits }: PortalVisitHistoryProps) => {
  const { t, i18n } = useTranslation();

  if (!visits) {
    return <p className="text-sm text-grey-40 dark:text-grey-80">{t("common.loading", "Loading…")}</p>;
  }

  if (visits.length === 0) {
    return (
      <p className="text-sm text-grey-40 dark:text-grey-80">
        {t("portal.noVisits")}
      </p>
    );
  }

  const formatDate = (date: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      weekday: "short",
      day: "numeric",
      month: "long",
    }).format(new Date(`${date}T00:00:00`));

  return (
    <ul className="flex flex-col gap-2">
      {visits.map((visit) => (
        <li
          key={visit.visitId}
          className="with-border flex items-center justify-between gap-3 rounded-2xl bg-white p-3 dark:bg-primary-20"
        >
          <div>
            <p className="font-medium text-grey-20 dark:text-white">{visit.title}</p>
            <p className="text-sm text-grey-40 dark:text-grey-80">
              {formatDate(visit.date)}
              {visit.coveredByPayment && <> · {t("portal.visitCovered", "Covered by payment")}</>}
            </p>
          </div>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
              visitStatusStyles[visit.status] ?? fallbackVisitStatusStyle
            }`}
          >
            {visit.isLateCancellationFee
              ? t("portal.visitStatus.CancelledLateFee")
              : t(`portal.visitStatus.${visit.status}`, visit.status)}
          </span>
        </li>
      ))}
    </ul>
  );
};

export default PortalVisitHistory;
